/**
 * Flow360 Account Browser — browse Flow360 simulation hierarchy and import into Thread.
 *
 * Layout:
 * - Left sidebar: searchable, infinitely-scrolling project list with rich cards
 * - Main area: expandable artifact tree (geometry → surface mesh → volume mesh → case)
 * - Bottom bar: import action
 */

import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Dialog,
  Flex,
  ScrollArea,
  Select,
  Text,
  TextField,
} from "@radix-ui/themes";

import {
  fetchFlow360Folders,
  fetchFlow360Geometries,
  fetchFlow360SurfaceMeshes,
  fetchFlow360VolumeMeshes,
  fetchFlow360Cases,
  fetchFlow360CaseResults,
  fetchProjectThumbnail,
  getCurrentThreadPath,
  importFlow360Data,
  clearFlow360Session,
  getFlow360AccessToken,
  getFlow360UserEmail,
  getStatusColor,
  redirectToFlow360Login,
} from "../flow360/api";
import type { FolderPage } from "../flow360/api";
import type {
  Flow360Folder,
  Flow360Geometry,
  Flow360SurfaceMesh,
  Flow360VolumeMesh,
  Flow360Case,
  CaseResults,
  Flow360ThreadProjectLink,
} from "../flow360/types";
import {
  fetchProjects as fetchThreadProjects,
  type Project as ThreadProject,
} from "../api/projects";
import { ThreadLogo } from "../components/ThreadLogo";
import { FeedbackDevBadge } from "../components/feedback/FeedbackReporter";
import { authClient } from "../lib/auth";
import { Icons } from "../features/thread-v4/icons";
import "../features/thread-v5/thread-v5.css";

const PAGE_SIZE = 100;

export type ProjectSort =
  | "created-desc"
  | "created-asc"
  | "name-asc"
  | "name-desc"
  | "cases-desc"
  | "geometries-desc"
  | "solver-desc"
  | "status-asc";

type ProjectStatusFilter = "all" | "success" | "warning" | "error" | "default";
type ProjectTypeFilter = "all" | "Folder" | "Geometry" | "SurfaceMesh" | "VolumeMesh";
type ProjectCaseFilter = "all" | "with-cases" | "without-cases";

export function isFlow360WorkspaceFolder(folder: Flow360Folder): boolean {
  return folder.type === "Folder" || (folder.id.startsWith("folder-") && !folder.rootItemType);
}

function folderCaseCount(folder: Flow360Folder): number {
  return folder.statistics?.Case?.count ?? 0;
}

function folderGeometryCount(folder: Flow360Folder): number {
  return folder.statistics?.Geometry?.count ?? 0;
}

function folderStatus(folder: Flow360Folder): string | null | undefined {
  return folder.rootItemDisplayStatus ?? folder.rootItemStatus;
}

function threadProjectLinks(folder: Flow360Folder): Flow360ThreadProjectLink[] {
  return folder.threadProjectLinks ?? [];
}

function linkedThreadProjectIds(links: readonly Flow360ThreadProjectLink[] = []): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const link of links) {
    if (!link.projectId || seen.has(link.projectId)) continue;
    seen.add(link.projectId);
    ids.push(link.projectId);
  }
  return ids;
}

function threadProjectLinkSummary(links: readonly Flow360ThreadProjectLink[]): string {
  if (links.length === 1) return "Linked to 1 Thread project";
  return `Linked to ${links.length} Thread projects`;
}

function threadProjectLinkDetail(link: Flow360ThreadProjectLink): string {
  const parts = [];
  if (link.caseCount > 0) parts.push(`${link.caseCount} case${link.caseCount === 1 ? "" : "s"}`);
  if (link.nodeCount > 0) parts.push(`${link.nodeCount} node${link.nodeCount === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

function normalizeSearchText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[_./:-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function folderSearchText(folder: Flow360Folder): string {
  const status = folderStatus(folder);
  const caseCount = folderCaseCount(folder);
  const geoCount = folderGeometryCount(folder);
  const raw = [
    folder.id,
    folder.name,
    folder.description,
    folder.solverVersion,
    folder.type,
    folder.rootItemType,
    status,
    getStatusColor(status ?? ""),
    folder.createdAt,
    folder.updatedAt,
    `${caseCount} case${caseCount === 1 ? "" : "s"}`,
    `${geoCount} geo`,
    ...(folder.tags ?? []),
  ]
    .filter(Boolean)
    .join(" ");
  return `${raw.toLowerCase()} ${normalizeSearchText(raw)}`;
}

function compareNullableText(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  return (a ?? "").localeCompare(b ?? "", undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function compareDateDesc(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  const aTime = a ? new Date(a).getTime() : 0;
  const bTime = b ? new Date(b).getTime() : 0;
  return bTime - aTime;
}

function sortFolders(folders: Flow360Folder[], sort: ProjectSort): Flow360Folder[] {
  const sorted = [...folders];
  sorted.sort((a, b) => {
    const aIsFolder = isFlow360WorkspaceFolder(a);
    const bIsFolder = isFlow360WorkspaceFolder(b);
    if (aIsFolder !== bIsFolder) return aIsFolder ? -1 : 1;

    const fallback =
      compareDateDesc(a.createdAt, b.createdAt) ||
      compareNullableText(a.name, b.name);
    switch (sort) {
      case "created-asc":
        return (
          compareDateDesc(b.createdAt, a.createdAt) ||
          compareNullableText(a.name, b.name)
        );
      case "name-asc":
        return compareNullableText(a.name, b.name) || fallback;
      case "name-desc":
        return compareNullableText(b.name, a.name) || fallback;
      case "cases-desc":
        return folderCaseCount(b) - folderCaseCount(a) || fallback;
      case "geometries-desc":
        return folderGeometryCount(b) - folderGeometryCount(a) || fallback;
      case "solver-desc":
        return compareNullableText(b.solverVersion, a.solverVersion) || fallback;
      case "status-asc":
        return compareNullableText(folderStatus(a), folderStatus(b)) || fallback;
      case "created-desc":
      default:
        return fallback;
    }
  });
  return sorted;
}

export type Flow360FolderTreeNode = {
  folder: Flow360Folder;
  children: Flow360FolderTreeNode[];
  depth: number;
  ancestors: Flow360Folder[];
  path: Flow360Folder[];
};

export function buildFlow360FolderTree(
  folders: Flow360Folder[],
  sort: ProjectSort = "created-desc",
): Flow360FolderTreeNode[] {
  type MutableNode = {
    folder: Flow360Folder;
    children: MutableNode[];
  };

  const nodeById = new Map<string, MutableNode>();
  for (const folder of folders) {
    nodeById.set(folder.id, { folder, children: [] });
  }

  const roots: MutableNode[] = [];
  for (const node of nodeById.values()) {
    const parentId = node.folder.parentFolderId?.trim();
    const parent = parentId && parentId !== node.folder.id ? nodeById.get(parentId) : null;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortNodes = (nodes: MutableNode[]): MutableNode[] => {
    const nodeLookup = new Map(nodes.map((node) => [node.folder.id, node]));
    return sortFolders(nodes.map((node) => node.folder), sort)
      .map((folder) => nodeLookup.get(folder.id))
      .filter((node): node is MutableNode => Boolean(node))
      .map((node) => ({
        ...node,
        children: sortNodes(node.children),
      }));
  };

  const freezeNodes = (
    nodes: MutableNode[],
    depth: number,
    ancestors: Flow360Folder[],
  ): Flow360FolderTreeNode[] =>
    nodes.map((node) => {
      const path = [...ancestors, node.folder];
      return {
        folder: node.folder,
        children: freezeNodes(node.children, depth + 1, path),
        depth,
        ancestors,
        path,
      };
    });

  return freezeNodes(sortNodes(roots), 0, []);
}

export function filterFlow360FolderTree(
  nodes: Flow360FolderTreeNode[],
  predicate: (node: Flow360FolderTreeNode) => boolean,
): Flow360FolderTreeNode[] {
  return nodes
    .map((node) => {
      const children = filterFlow360FolderTree(node.children, predicate);
      if (!predicate(node) && children.length === 0) return null;
      return { ...node, children };
    })
    .filter((node): node is Flow360FolderTreeNode => Boolean(node));
}

export function flattenFlow360FolderTree(
  nodes: Flow360FolderTreeNode[],
  collapsedFolderIds: ReadonlySet<string> = new Set<string>(),
): Flow360FolderTreeNode[] {
  const visible: Flow360FolderTreeNode[] = [];
  const visit = (node: Flow360FolderTreeNode) => {
    visible.push(node);
    if (!collapsedFolderIds.has(node.folder.id)) {
      node.children.forEach(visit);
    }
  };
  nodes.forEach(visit);
  return visible;
}

export function findFlow360FolderNode(
  nodes: Flow360FolderTreeNode[],
  folderId: string | null | undefined,
): Flow360FolderTreeNode | null {
  if (!folderId) return null;
  for (const node of nodes) {
    if (node.folder.id === folderId) return node;
    const child = findFlow360FolderNode(node.children, folderId);
    if (child) return child;
  }
  return null;
}

function folderTreeSearchText(node: Flow360FolderTreeNode): string {
  return [node.folder, ...node.ancestors].map(folderSearchText).join(" ");
}

function matchesFolderTreeSearch(node: Flow360FolderTreeNode, search: string): boolean {
  const terms = normalizeSearchText(search).split(" ").filter(Boolean);
  if (terms.length === 0) return true;
  const haystack = folderTreeSearchText(node);
  return terms.every((term) => haystack.includes(term));
}

function matchesFolderTreeControls(
  node: Flow360FolderTreeNode,
  search: string,
  statusFilter: ProjectStatusFilter,
  typeFilter: ProjectTypeFilter,
  caseFilter: ProjectCaseFilter,
): boolean {
  const folder = node.folder;
  if (!matchesFolderTreeSearch(node, search)) return false;
  if (
    statusFilter !== "all" &&
    getStatusColor(folderStatus(folder) ?? "") !== statusFilter
  ) {
    return false;
  }
  if (
    typeFilter !== "all" &&
    (typeFilter === "Folder"
      ? !isFlow360WorkspaceFolder(folder)
      : folder.rootItemType !== typeFilter)
  ) {
    return false;
  }
  if (caseFilter === "with-cases" && folderCaseCount(folder) === 0) {
    return false;
  }
  if (caseFilter === "without-cases" && folderCaseCount(folder) > 0) {
    return false;
  }
  return true;
}

// ─── SVG icons ──────────────────────────────────────────────────────────────

function GeometryIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path
        d="M8 1L14.5 5v6L8 15 1.5 11V5L8 1z"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="none"
      />
      <path d="M8 1v14M1.5 5L14.5 11M14.5 5L1.5 11" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
    </svg>
  );
}

function SurfaceMeshIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M1 5.5h14M1 10.5h14M5.5 1v14M10.5 1v14" stroke="currentColor" strokeWidth="0.7" opacity="0.5" />
    </svg>
  );
}

function VolumeMeshIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M3 12L8 15l5-3V5L8 2 3 5v7z" stroke="currentColor" strokeWidth="1.2" />
      <path d="M3 5l5 3 5-3M8 8v7" stroke="currentColor" strokeWidth="0.8" opacity="0.6" />
    </svg>
  );
}

function CaseIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5 3V2a1 1 0 011-1h4a1 1 0 011 1v1" stroke="currentColor" strokeWidth="1.2" />
      <path d="M2 7h12" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
    </svg>
  );
}

// ─── Relative time ──────────────────────────────────────────────────────────

function relativeTime(iso: string | undefined | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

// ─── Status badge ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return null;
  const color = getStatusColor(status);
  const radixColor =
    color === "success"
      ? "green"
      : color === "warning"
        ? "orange"
        : color === "error"
          ? "red"
          : "gray";
  return (
    <Badge color={radixColor} variant="soft" size="1">
      {status}
    </Badge>
  );
}

function StatusDot({ status }: { status: string | null | undefined }) {
  if (!status) return null;
  const color = getStatusColor(status);
  const hex =
    color === "success"
      ? "var(--green-9)"
      : color === "warning"
        ? "var(--orange-9)"
        : color === "error"
          ? "var(--red-9)"
          : "var(--gray-8)";
  return (
    <Box
      style={{
        width: 7,
        height: 7,
        borderRadius: "50%",
        background: hex,
        flexShrink: 0,
      }}
    />
  );
}

function Flow360RunConsentNotice() {
  return (
    <div className="flow360-v5-notice is-amber">
      <Text as="p" size="1" style={{ lineHeight: 1.45, margin: 0 }}>
        Signing in with Flow360 exchanges and stores your Flow360 API key in
        Thread. Thread can then submit and monitor Flow360 jobs for you inside
        shared projects.
      </Text>
    </div>
  );
}

// ─── Project thumbnail ──────────────────────────────────────────────────────

function ProjectThumbnail({ folder }: { folder: Flow360Folder }) {
  const { data } = useQuery({
    queryKey: ["flow360", "thumbnail", folder.id],
    queryFn: () => fetchProjectThumbnail(folder.id),
    enabled: !!folder.thumbnailPath,
    staleTime: 30 * 60_000,
    retry: false,
  });

  if (data?.url) {
    return (
      <Box
        className="flow360-v5-thumb"
      >
        <img
          src={data.url}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          loading="lazy"
        />
      </Box>
    );
  }

  if (isFlow360WorkspaceFolder(folder)) {
    return (
      <Flex
        className="flow360-v5-thumb"
        align="center"
        justify="center"
      >
        <Icons.Folder size={18} />
      </Flex>
    );
  }

  const icon = folder.rootItemType;
  return (
    <Flex
      className="flow360-v5-thumb"
      align="center"
      justify="center"
    >
      {icon === "Geometry" && <GeometryIcon size={18} />}
      {icon === "SurfaceMesh" && <SurfaceMeshIcon size={18} />}
      {icon === "VolumeMesh" && <VolumeMeshIcon size={18} />}
      {!icon && <GeometryIcon size={18} />}
    </Flex>
  );
}

// ─── Project card (sidebar row) ─────────────────────────────────────────────

function ProjectCard({
  node,
  selected,
  onClick,
  onToggle,
  expanded,
}: {
  node: Flow360FolderTreeNode;
  selected: boolean;
  onClick: () => void;
  onToggle: () => void;
  expanded: boolean;
}) {
  const { folder } = node;
  const isWorkspaceFolder = isFlow360WorkspaceFolder(folder);
  const stats = folder.statistics;
  const links = threadProjectLinks(folder);
  const caseCount = stats?.Case?.count ?? 0;
  const geoCount = stats?.Geometry?.count ?? 0;
  const childCount = node.children.length;
  const hasChildren = childCount > 0;
  const pathLabel = node.ancestors.map((ancestor) => ancestor.name).join(" / ");
  const rowStyle = {
    "--flow360-folder-indent": `${node.depth * 14}px`,
  } as CSSProperties;

  return (
    <div
      className={`flow360-v5-project-card ${selected ? "is-selected" : ""}`}
      style={rowStyle}
    >
      <button
        type="button"
        className="flow360-v5-project-card-toggle"
        disabled={!hasChildren}
        aria-label={expanded ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
        onClick={onToggle}
      >
        {hasChildren ? (
          expanded ? <Icons.ChevronDown size={13} /> : <Icons.ChevronRight size={13} />
        ) : (
          <span className="flow360-v5-project-card-toggle-spacer" />
        )}
      </button>
      <button
        type="button"
        className="flow360-v5-project-card-main"
        onClick={onClick}
      >
        <Flex gap="2" align="start">
          <ProjectThumbnail folder={folder} />
          <Box style={{ minWidth: 0, flex: 1 }}>
            <Flex align="center" gap="1" mb="1">
              <StatusDot status={folder.rootItemDisplayStatus ?? folder.rootItemStatus} />
              <Text
                size="2"
                weight={selected ? "bold" : "medium"}
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {folder.name}
              </Text>
            </Flex>
            {pathLabel && (
              <Text className="flow360-v5-project-path" size="1" color="gray">
                {pathLabel}
              </Text>
            )}
            <Flex gap="2" align="center" wrap="wrap">
              {hasChildren && (
                <Text size="1" color="gray">
                  {childCount} item{childCount !== 1 ? "s" : ""}
                </Text>
              )}
              {isWorkspaceFolder && (
                <Text size="1" color="gray">
                  Folder
                </Text>
              )}
              {folder.rootItemType && (
                <Text size="1" color="gray">
                  {folder.rootItemType}
                </Text>
              )}
              {geoCount > 0 && (
                <Text size="1" color="gray">
                  {geoCount} geo
                </Text>
              )}
              {caseCount > 0 && (
                <Text size="1" color="gray">
                  {caseCount} case{caseCount !== 1 ? "s" : ""}
                </Text>
              )}
              {folder.solverVersion && (
                <Text size="1" color="gray">
                  {folder.solverVersion}
                </Text>
              )}
              {folder.createdAt && (
                <Text size="1" color="gray">
                  {relativeTime(folder.createdAt)}
                </Text>
              )}
              {links.length > 0 && (
                <Badge className="flow360-v5-link-badge" variant="soft" color="green" size="1">
                  {threadProjectLinkSummary(links)}
                </Badge>
              )}
            </Flex>
            {folder.tags && folder.tags.length > 0 && (
              <Flex gap="1" mt="1" wrap="wrap">
                {folder.tags.slice(0, 3).map((t) => (
                  <Badge key={t} variant="outline" size="1" color="gray">
                    {t}
                  </Badge>
                ))}
              </Flex>
            )}
          </Box>
        </Flex>
      </button>
    </div>
  );
}

// ─── Inline result chips ────────────────────────────────────────────────────

function ResultChips({ results }: { results: CaseResults | undefined }) {
  if (!results?.outputs) return null;
  const keys = ["CL", "CD", "CMy", "CMpitching"];
  const shown = keys.filter((k) => results.outputs![k] != null);
  if (shown.length === 0) return null;
  return (
    <Flex gap="2" wrap="wrap">
      {shown.map((k) => (
        <Badge key={k} variant="outline" size="1">
          {k}={Number(results.outputs![k]).toPrecision(4)}
        </Badge>
      ))}
      {results.inputs?.alpha != null && (
        <Badge variant="outline" color="blue" size="1">
          α={results.inputs.alpha}°
        </Badge>
      )}
      {results.inputs?.mach != null && (
        <Badge variant="outline" color="blue" size="1">
          M={results.inputs.mach}
        </Badge>
      )}
    </Flex>
  );
}

// ─── Expandable case row ────────────────────────────────────────────────────

function CaseRow({ c }: { c: Flow360Case }) {
  const [expanded, setExpanded] = useState(false);

  const { data: results } = useQuery({
    queryKey: ["flow360", "case-results", c.id],
    queryFn: () => fetchFlow360CaseResults(c.id),
    enabled: c.status === "completed",
    staleTime: 5 * 60_000,
  });

  return (
    <Box ml="7" mb="1">
      <button
        className="flow360-v5-tree-row"
        type="button"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <Icons.ChevronDown size={13} /> : <Icons.ChevronRight size={13} />}
        <CaseIcon />
        <Text size="2" weight="medium">
          {c.name}
        </Text>
        <StatusBadge status={c.status} />
        <ResultChips results={results} />
      </button>

      {expanded && results && (
        <Box ml="4" mb="2" p="2" style={{ background: "var(--gray-a3)", borderRadius: 6 }}>
          {results.inputs && (
            <Box mb="2">
              <Text size="1" color="gray" weight="bold">
                Inputs
              </Text>
              <Flex gap="3" mt="1" wrap="wrap">
                {Object.entries(results.inputs).map(([k, v]) => (
                  <Text key={k} size="1">
                    {k}: {typeof v === "number" ? v.toPrecision(5) : String(v)}
                  </Text>
                ))}
              </Flex>
            </Box>
          )}
          {results.outputs && (
            <Box>
              <Text size="1" color="gray" weight="bold">
                Force Coefficients
              </Text>
              <Box
                mt="1"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                  gap: 4,
                }}
              >
                {Object.entries(results.outputs).map(([k, v]) => (
                  <Text key={k} size="1" style={{ fontFamily: "monospace" }}>
                    {k}: {typeof v === "number" ? v.toPrecision(6) : String(v)}
                  </Text>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── Volume mesh group ──────────────────────────────────────────────────────

function VolumeMeshGroup({ vm }: { vm: Flow360VolumeMesh }) {
  const [open, setOpen] = useState(false);

  const { data: cases } = useQuery({
    queryKey: ["flow360", "cases", vm.id],
    queryFn: () => fetchFlow360Cases({ volumeMeshId: vm.id }),
    enabled: open,
  });

  return (
    <Box ml="5" mb="1">
      <button
        className="flow360-v5-tree-row"
        type="button"
        onClick={() => setOpen(!open)}
      >
        {open ? <Icons.ChevronDown size={13} /> : <Icons.ChevronRight size={13} />}
        <VolumeMeshIcon />
        <Text size="2">{vm.name}</Text>
        <StatusBadge status={vm.status} />
      </button>
      {open && cases?.map((c) => <CaseRow key={c.id} c={c} />)}
    </Box>
  );
}

// ─── Surface mesh group ─────────────────────────────────────────────────────

function SurfaceMeshGroup({ sm }: { sm: Flow360SurfaceMesh }) {
  const [open, setOpen] = useState(false);

  const { data: volumeMeshes } = useQuery({
    queryKey: ["flow360", "volume-meshes", sm.id],
    queryFn: () => fetchFlow360VolumeMeshes({ surfaceMeshId: sm.id }),
    enabled: open,
  });

  return (
    <Box ml="3" mb="1">
      <button
        className="flow360-v5-tree-row"
        type="button"
        onClick={() => setOpen(!open)}
      >
        {open ? <Icons.ChevronDown size={13} /> : <Icons.ChevronRight size={13} />}
        <SurfaceMeshIcon />
        <Text size="2">{sm.name}</Text>
        <StatusBadge status={sm.status} />
      </button>
      {open &&
        volumeMeshes?.map((vm) => <VolumeMeshGroup key={vm.id} vm={vm} />)}
    </Box>
  );
}

// ─── Geometry group ─────────────────────────────────────────────────────────

function GeometryGroup({ geom }: { geom: Flow360Geometry }) {
  const [open, setOpen] = useState(false);

  const { data: surfaceMeshes } = useQuery({
    queryKey: ["flow360", "surface-meshes", geom.id],
    queryFn: () => fetchFlow360SurfaceMeshes({ geometryId: geom.id }),
    enabled: open,
  });

  return (
    <article className="flow360-v5-tree-card">
      <button
        className="flow360-v5-tree-row is-root"
        type="button"
        onClick={() => setOpen(!open)}
      >
        {open ? <Icons.ChevronDown size={13} /> : <Icons.ChevronRight size={13} />}
        <GeometryIcon />
        <Text size="3" weight="medium">
          {geom.name}
        </Text>
        <StatusBadge status={geom.status} />
      </button>
      {open &&
        surfaceMeshes?.map((sm) => <SurfaceMeshGroup key={sm.id} sm={sm} />)}
    </article>
  );
}

// ─── Import dialog ──────────────────────────────────────────────────────────

export function resolveImportTargetProjectId(
  current: string | undefined,
  preferredProjectId: string | undefined,
  threadProjects: Pick<ThreadProject, "id">[],
  linkedProjectIds: readonly string[] = [],
): string | undefined {
  const projectIds = new Set([
    ...threadProjects.map((project) => project.id),
    ...linkedProjectIds,
  ]);
  if (preferredProjectId && current === preferredProjectId) {
    return current;
  }
  if (current && projectIds.has(current)) {
    return current;
  }
  const linkedProjectId = linkedProjectIds.find((projectId) => projectIds.has(projectId));
  if (linkedProjectId) return linkedProjectId;
  if (preferredProjectId) {
    return preferredProjectId;
  }
  return threadProjects[0]?.id;
}

function ImportDialog({
  folderId,
  folderName,
  threadLinks = [],
  preferredProjectId,
  returnTo,
  open,
  onClose,
}: {
  folderId: string;
  folderName: string;
  threadLinks?: Flow360ThreadProjectLink[];
  preferredProjectId?: string;
  returnTo?: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const [newProjectName, setNewProjectName] = useState(folderName);
  const [targetMode, setTargetMode] = useState<"existing" | "new">("existing");
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: threadProjectsData, isLoading: projectsLoading } = useQuery<ThreadProject[]>({
    queryKey: ["projects"],
    queryFn: fetchThreadProjects,
    enabled: open,
  });
  const threadProjects = useMemo(() => threadProjectsData ?? [], [threadProjectsData]);
  const linkedProjectIds = useMemo(() => linkedThreadProjectIds(threadLinks), [threadLinks]);
  const linkedProjectIdSet = useMemo(() => new Set(linkedProjectIds), [linkedProjectIds]);
  const projectOptions = useMemo(() => {
    const options = threadProjects.map((project) => ({
      id: project.id,
      name: project.name,
      linked: linkedProjectIdSet.has(project.id),
    }));
    const seen = new Set(options.map((option) => option.id));
    if (preferredProjectId && !seen.has(preferredProjectId)) {
      options.unshift({
        id: preferredProjectId,
        name: "Current Thread project",
        linked: linkedProjectIdSet.has(preferredProjectId),
      });
      seen.add(preferredProjectId);
    }
    for (const link of threadLinks) {
      if (seen.has(link.projectId)) continue;
      options.push({
        id: link.projectId,
        name: link.projectName || "Linked Thread project",
        linked: true,
      });
      seen.add(link.projectId);
    }
    return options;
  }, [linkedProjectIdSet, preferredProjectId, threadLinks, threadProjects]);

  useEffect(() => {
    if (!open) return;
    setNewProjectName(folderName);
    setTargetMode(preferredProjectId || projectOptions.length > 0 ? "existing" : "new");
    if (preferredProjectId) setSelectedProjectId(preferredProjectId);
  }, [folderName, open, preferredProjectId, projectOptions.length]);

  useEffect(() => {
    if (!open || projectsLoading) return;
    if (projectOptions.length === 0 && !preferredProjectId) {
      setTargetMode("new");
      setSelectedProjectId(undefined);
      return;
    }
    setSelectedProjectId((current) => (
      resolveImportTargetProjectId(current, preferredProjectId, threadProjects, linkedProjectIds)
    ));
  }, [linkedProjectIds, open, preferredProjectId, projectOptions.length, projectsLoading, threadProjects]);

  const mutation = useMutation({
    mutationFn: importFlow360Data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["flow360", "folders"] });
      const destination =
        returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//")
          ? appendQuery(returnTo, { importJob: data.job_id })
          : threadV5ProjectPath(data.project_id, { importJob: data.job_id });
      navigate(destination);
    },
  });

  const canImport =
    targetMode === "existing"
      ? !!selectedProjectId
      : !!newProjectName.trim();

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Content className="flow360-v5-dialog" maxWidth="450px">
        <Dialog.Title>Import to Thread</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="4">
          Import completed cases from &quot;{folderName}&quot; into a Thread project.
          Multiple Flow360 projects can share one Thread workspace and appear
          together in the flat table.
        </Dialog.Description>

        {threadLinks.length > 0 && (
          <div className="flow360-v5-link-panel">
            <Text size="2" weight="medium">
              Already linked in Thread
            </Text>
            <div>
              {threadLinks.map((link) => (
                <Link
                  key={link.projectId}
                  to={threadV5ProjectPath(link.projectId)}
                >
                  <span>{link.projectName || "Thread project"}</span>
                  {threadProjectLinkDetail(link) && <em>{threadProjectLinkDetail(link)}</em>}
                </Link>
              ))}
            </div>
          </div>
        )}

        <Box mb="4">
          <Text size="2" weight="bold" mb="1">
            Destination
          </Text>
          <Select.Root
            value={targetMode}
            onValueChange={(value) => setTargetMode(value as "existing" | "new")}
          >
            <Select.Trigger style={{ width: "100%" }} />
            <Select.Content>
              <Select.Item value="existing" disabled={projectOptions.length === 0}>
                Existing Thread project
              </Select.Item>
              <Select.Item value="new">New Thread project</Select.Item>
            </Select.Content>
          </Select.Root>
        </Box>

        {targetMode === "existing" ? (
          <Box mb="4">
            <Text size="2" weight="bold" mb="1">
              Thread project
            </Text>
            <Select.Root
              value={selectedProjectId}
              onValueChange={setSelectedProjectId}
              disabled={projectsLoading || projectOptions.length === 0}
            >
              <Select.Trigger
                placeholder={projectsLoading ? "Loading projects..." : "Select project"}
                style={{ width: "100%" }}
              />
              <Select.Content>
                {projectOptions.map((project) => (
                  <Select.Item key={project.id} value={project.id}>
                    {project.name}{project.linked ? " · linked" : ""}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Box>
        ) : (
          <Box mb="4">
            <Text size="2" weight="bold" mb="1">
              New project name
            </Text>
            <TextField.Root
              value={newProjectName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewProjectName(e.target.value)
              }
              placeholder="Project name"
            />
          </Box>
        )}

        {mutation.isError && (
          <Box
            mb="3"
            p="2"
            style={{ background: "var(--red-a3)", borderRadius: 6 }}
          >
            <Text size="2" color="red">
              {(mutation.error as Error).message}
            </Text>
          </Box>
        )}

        <Flex gap="3" justify="end" mt="4">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            onClick={() => {
              const base = {
                folder_id: folderId,
                folder_name: folderName,
                branch: "main",
              };
              if (targetMode === "existing") {
                if (!selectedProjectId) return;
                mutation.mutate({ ...base, project_id: selectedProjectId });
              } else {
                mutation.mutate({ ...base, project_name: newProjectName || folderName });
              }
            }}
            disabled={mutation.isPending || !canImport}
          >
            {mutation.isPending ? "Starting import..." : "Import"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function Flow360ThreadLinksPanel({ links }: { links: Flow360ThreadProjectLink[] }) {
  if (links.length === 0) return null;
  return (
    <div className="flow360-v5-thread-links">
      <div>
        <span className="v5-eyebrow">Thread link</span>
        <strong>{threadProjectLinkSummary(links)}</strong>
      </div>
      <div className="flow360-v5-thread-link-list">
        {links.map((link) => (
          <Link
            key={link.projectId}
            to={threadV5ProjectPath(link.projectId)}
          >
            <span>{link.projectName || "Thread project"}</span>
            {threadProjectLinkDetail(link) && <em>{threadProjectLinkDetail(link)}</em>}
          </Link>
        ))}
      </div>
    </div>
  );
}

function appendQuery(path: string, patch: Record<string, string>): string {
  const [pathnameAndSearch, hash = ""] = path.split("#", 2);
  const [pathname, search = ""] = pathnameAndSearch!.split("?", 2);
  const params = new URLSearchParams(search);
  for (const [key, value] of Object.entries(patch)) {
    params.set(key, value);
  }
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ""}${hash ? `#${hash}` : ""}`;
}

function threadV5ProjectPath(projectId: string, options: { importJob?: string | null } = {}): string {
  const params = new URLSearchParams();
  if (options.importJob) params.set("importJob", options.importJob);
  const query = params.toString();
  return `/v5/${encodeURIComponent(projectId)}${query ? `?${query}` : ""}`;
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function Flow360Browser() {
  const [searchParams] = useSearchParams();
  const [selectedFolder, setSelectedFolder] = useState<Flow360Folder | null>(
    null,
  );
  const [importOpen, setImportOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<ProjectSort>("created-desc");
  const [statusFilter, setStatusFilter] = useState<ProjectStatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<ProjectTypeFilter>("all");
  const [caseFilter, setCaseFilter] = useState<ProjectCaseFilter>("all");
  const [collapsedFolderIds, setCollapsedFolderIds] = useState<Set<string>>(
    () => new Set(),
  );

  const {
    data: foldersData,
    isLoading: foldersLoading,
    isError: foldersError,
    error: foldersErrorObj,
    fetchNextPage,
    refetch: refetchFolders,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<FolderPage>({
    queryKey: ["flow360", "folders"],
    queryFn: ({ pageParam }) =>
      fetchFlow360Folders({
        page: pageParam as number,
        size: PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;
      return nextPage * lastPage.size < lastPage.total ? nextPage : undefined;
    },
    retry: false,
  });

  const tokenExpired =
    foldersError &&
    (foldersErrorObj?.message?.includes("401") ||
      foldersErrorObj?.message?.toLowerCase().includes("expired") ||
      foldersErrorObj?.message?.toLowerCase().includes("invalid") ||
      foldersErrorObj?.message?.toLowerCase().includes("reconnect"));
  const hasFlow360BrowserToken = !!getFlow360AccessToken();
  const flow360UserEmail = getFlow360UserEmail();
  const flow360ApiKeyStored = sessionStorage.getItem("flow360_api_key_stored");
  const preferredProjectId =
    searchParams.get("thread_project_id") ?? searchParams.get("project_id") ?? undefined;
  const returnTo = searchParams.get("return_to");
  const connectFlow360 = useCallback(() => {
    redirectToFlow360Login(getCurrentThreadPath());
  }, []);
  const handleLogout = useCallback(async () => {
    clearFlow360Session();
    window.dispatchEvent(new Event("flow360-auth-ready"));
    await authClient.auth.signOut();
  }, []);

  const allFolders = useMemo(
    () => foldersData?.pages.flatMap((p) => p.projects) ?? [],
    [foldersData],
  );
  const totalProjects = foldersData?.pages[0]?.total ?? 0;

  const hasProjectControls =
    !!search.trim() ||
    sort !== "created-desc" ||
    statusFilter !== "all" ||
    typeFilter !== "all" ||
    caseFilter !== "all";

  // Load all remaining pages so local hierarchy building, search, sorting, and
  // filters operate on the whole Flow360 project/folder set.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const folderTree = useMemo(
    () => buildFlow360FolderTree(allFolders, sort),
    [allFolders, sort],
  );
  const filteredFolderTree = useMemo(
    () =>
      filterFlow360FolderTree(folderTree, (node) =>
        matchesFolderTreeControls(
          node,
          search,
          statusFilter,
          typeFilter,
          caseFilter,
        ),
      ),
    [caseFilter, folderTree, search, statusFilter, typeFilter],
  );
  const visibleFolderNodes = useMemo(
    () =>
      flattenFlow360FolderTree(
        filteredFolderTree,
        hasProjectControls ? undefined : collapsedFolderIds,
      ),
    [collapsedFolderIds, filteredFolderTree, hasProjectControls],
  );
  const folders = useMemo(
    () => visibleFolderNodes.map((node) => node.folder),
    [visibleFolderNodes],
  );
  const selectedFolderNode = useMemo(
    () => findFlow360FolderNode(folderTree, selectedFolder?.id),
    [folderTree, selectedFolder?.id],
  );
  const selectedIsWorkspaceFolder = selectedFolder
    ? isFlow360WorkspaceFolder(selectedFolder)
    : false;

  const resetProjectControls = useCallback(() => {
    setSearch("");
    setSort("created-desc");
    setStatusFilter("all");
    setTypeFilter("all");
    setCaseFilter("all");
  }, []);
  const toggleFolderCollapsed = useCallback((folderId: string) => {
    setCollapsedFolderIds((current) => {
      const next = new Set(current);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement>(null);
  const handleScroll = useCallback(() => {
    if (!sentinelRef.current || !hasNextPage || isFetchingNextPage) return;
    const el = sentinelRef.current;
    const parent = el.parentElement;
    if (!parent) return;
    const rect = el.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    if (rect.top < parentRect.bottom + 200) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const viewport = el.querySelector("[data-radix-scroll-area-viewport]");
    if (!viewport) return;
    const handler = () => handleScroll();
    viewport.addEventListener("scroll", handler, { passive: true });
    return () => viewport.removeEventListener("scroll", handler);
  }, [handleScroll]);

  // Also trigger check on data change
  useEffect(() => {
    handleScroll();
  }, [folders.length, handleScroll]);

  const { data: geometries, isLoading: geoLoading } = useQuery({
    queryKey: ["flow360", "geometries", selectedFolder?.id],
    queryFn: () => fetchFlow360Geometries(selectedFolder!.id),
    enabled: !!selectedFolder && !selectedIsWorkspaceFolder,
  });

  return (
    <div className="flow360-v5">
      <header className="v5-topbar flow360-v5-topbar">
        <Link to="/" className="v5-brand flow360-v5-brand-link">
          <ThreadLogo height={23} />
          <span className="v5-brand-version">V5</span>
        </Link>
        <div className="v5-crumb">
          <Link to="/" className="v5-text-button flow360-v5-back">
            <Icons.ChevronRight size={13} style={{ transform: "rotate(180deg)" }} />
            Projects
          </Link>
          <span className="v5-crumb-sep">/</span>
          <span className="v5-project-name">Flow360 Account</span>
          {foldersError ? (
            <span className="v5-chip flow360-v5-chip is-error">
              {tokenExpired ? "Not Authenticated" : "Error"}
            </span>
          ) : foldersLoading ? (
            <span className="v5-chip">
              Loading...
            </span>
          ) : (
            <span className="v5-chip is-green">
              {hasProjectControls
                ? `${folders.length} shown${hasNextPage ? " (loading...)" : ""}`
                : `${allFolders.length} of ${totalProjects} projects/folders`}
            </span>
          )}
        </div>
        <span className={`v5-live ${foldersError ? "is-pending" : "is-connected"}`}>
          <i />{foldersError ? "Flow360 needs attention" : "Flow360 connected"}
        </span>
        <FeedbackDevBadge />
        <div className="v5-topbar-spacer" />
        <button className="v5-text-button" type="button" onClick={() => void refetchFolders()}>
          <Icons.Refresh size={13} />
          Refresh
        </button>
        <button className="v5-text-button" type="button" onClick={() => void handleLogout()}>
          Log out
        </button>
      </header>

      {/* Body */}
      <div className="flow360-v5-body">
        {/* Folder sidebar */}
        <aside className="v5-panel flow360-v5-sidebar">
          <header className="v5-panel-header flow360-v5-sidebar-header">
            <div>
              <span className="v5-eyebrow">Flow360</span>
              <h2>Projects & folders</h2>
            </div>
          </header>
          <div className="flow360-v5-search-wrap">
            <label className="v5-search flow360-v5-search">
              <Icons.Search size={14} />
              <input
                placeholder="Search name, tag, version, status..."
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearch(e.target.value)
                }
              />
              {search && (
                <button
                  className="v5-text-button"
                  type="button"
                  onClick={() => setSearch("")}
                  style={{ padding: "2px 4px" }}
                >
                  Clear
                </button>
              )}
            </label>
            <Flex gap="2" mt="2">
              <Select.Root
                value={sort}
                onValueChange={(value) => setSort(value as ProjectSort)}
              >
                <Select.Trigger
                  aria-label="Sort projects"
                  style={{ flex: 1, minWidth: 0 }}
                />
                <Select.Content>
                  <Select.Item value="created-desc">Newest</Select.Item>
                  <Select.Item value="created-asc">Oldest</Select.Item>
                  <Select.Item value="name-asc">Name A-Z</Select.Item>
                  <Select.Item value="name-desc">Name Z-A</Select.Item>
                  <Select.Item value="cases-desc">Most cases</Select.Item>
                  <Select.Item value="geometries-desc">Most geometries</Select.Item>
                  <Select.Item value="solver-desc">Solver version</Select.Item>
                  <Select.Item value="status-asc">Status</Select.Item>
                </Select.Content>
              </Select.Root>
              <Select.Root
                value={statusFilter}
                onValueChange={(value) =>
                  setStatusFilter(value as ProjectStatusFilter)
                }
              >
                <Select.Trigger
                  aria-label="Filter projects by status"
                  style={{ flex: 1, minWidth: 0 }}
                />
                <Select.Content>
                  <Select.Item value="all">Any status</Select.Item>
                  <Select.Item value="success">Completed</Select.Item>
                  <Select.Item value="warning">Running</Select.Item>
                  <Select.Item value="error">Failed</Select.Item>
                  <Select.Item value="default">Other status</Select.Item>
                </Select.Content>
              </Select.Root>
            </Flex>
            <Flex gap="2" mt="2">
              <Select.Root
                value={typeFilter}
                onValueChange={(value) =>
                  setTypeFilter(value as ProjectTypeFilter)
                }
              >
                <Select.Trigger
                  aria-label="Filter projects by root asset"
                  style={{ flex: 1, minWidth: 0 }}
                />
                <Select.Content>
                  <Select.Item value="all">Any asset</Select.Item>
                  <Select.Item value="Folder">Folders</Select.Item>
                  <Select.Item value="Geometry">Geometry root</Select.Item>
                  <Select.Item value="SurfaceMesh">Surface mesh root</Select.Item>
                  <Select.Item value="VolumeMesh">Volume mesh root</Select.Item>
                </Select.Content>
              </Select.Root>
              <Select.Root
                value={caseFilter}
                onValueChange={(value) =>
                  setCaseFilter(value as ProjectCaseFilter)
                }
              >
                <Select.Trigger
                  aria-label="Filter projects by cases"
                  style={{ flex: 1, minWidth: 0 }}
                />
                <Select.Content>
                  <Select.Item value="all">Any cases</Select.Item>
                  <Select.Item value="with-cases">Has cases</Select.Item>
                  <Select.Item value="without-cases">No cases</Select.Item>
                </Select.Content>
              </Select.Root>
            </Flex>
            {hasProjectControls && (
              <Flex align="center" justify="between" mt="2">
                <Text size="1" color="gray">
                  {folders.length} shown from {allFolders.length} loaded
                </Text>
                <button
                  className="v5-text-button"
                  type="button"
                  onClick={resetProjectControls}
                >
                  Reset
                </button>
              </Flex>
            )}
          </div>
          <ScrollArea ref={scrollRef} className="flow360-v5-project-scroll">
            {foldersLoading && (
              <div className="flow360-v5-loading">
                <span className="flow360-v5-loading-dot" />
                <Text size="2">Loading projects...</Text>
              </div>
            )}
            {visibleFolderNodes.map((node) => (
              <ProjectCard
                key={node.folder.id}
                node={node}
                selected={selectedFolder?.id === node.folder.id}
                expanded={
                  hasProjectControls || !collapsedFolderIds.has(node.folder.id)
                }
                onToggle={() => toggleFolderCollapsed(node.folder.id)}
                onClick={() => setSelectedFolder(node.folder)}
              />
            ))}
            {/* Sentinel for infinite scroll */}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {isFetchingNextPage && (
              <Box px="3" py="2">
                <Text size="1" color="gray">
                  Loading more...
                </Text>
              </Box>
            )}
            {tokenExpired && (
              <div className="flow360-v5-sidebar-notice">
                <Text as="p" size="2">
                  Flow360 authentication failed. Reconnect your Flow360 account
                  and retry the project list.
                </Text>
                {flow360UserEmail && (
                  <Text as="p" size="1" color="gray" mt="1">
                    Last Flow360 identity: {flow360UserEmail}
                  </Text>
                )}
                <Box mt="3">
                  <Flow360RunConsentNotice />
                </Box>
                <Flex gap="2" mt="3" wrap="wrap">
                  <button className="v5-primary-button" type="button" onClick={connectFlow360}>
                    Reconnect Flow360
                  </button>
                  <button
                    className="v5-text-button"
                    type="button"
                    onClick={() => void refetchFolders()}
                  >
                    Retry
                  </button>
                </Flex>
              </div>
            )}
            {foldersError && !tokenExpired && (
              <div className="flow360-v5-sidebar-notice">
                <Text as="p" size="2">
                  Flow360 bridge is not connected locally.
                </Text>
                <Text as="p" size="1" color="gray" mt="1">
                  Thread sign-in is active, but browsing your Flow360 account
                  needs a separate Flow360 session or FLOW360_API_KEY on the
                  local backend.
                </Text>
                {foldersErrorObj?.message && (
                  <Text as="p" size="1" color="gray" mt="1">
                    {foldersErrorObj.message}
                  </Text>
                )}
                {hasFlow360BrowserToken && flow360UserEmail && (
                  <Text as="p" size="1" color="gray" mt="1">
                    Browser has a Flow360 token for {flow360UserEmail}, but the
                    backend rejected this request. Try reconnecting.
                  </Text>
                )}
                <Box mt="3">
                  <Flow360RunConsentNotice />
                </Box>
                <Flex gap="2" mt="3" wrap="wrap">
                  <button className="v5-primary-button" type="button" onClick={connectFlow360}>
                    Sign in with Flow360
                  </button>
                  <button
                    className="v5-text-button"
                    type="button"
                    onClick={() => void refetchFolders()}
                  >
                    Retry
                  </button>
                </Flex>
              </div>
            )}
            {!foldersError && flow360ApiKeyStored === "false" && (
              <div className="flow360-v5-sidebar-notice">
                <Text as="p" size="2">
                  Flow360 sign-in succeeded, but Thread could not store your
                  Flow360 API key yet. Reconnect before submitting runs.
                </Text>
                <Box mt="3">
                  <Flow360RunConsentNotice />
                </Box>
                <Flex gap="2" mt="3" wrap="wrap">
                  <button className="v5-primary-button" type="button" onClick={connectFlow360}>
                    Reconnect Flow360
                  </button>
                </Flex>
              </div>
            )}
            {!foldersLoading && !foldersError && !tokenExpired && folders.length === 0 && (
              <div className="flow360-v5-empty-list">
                <Text size="2">
                  No projects or folders found
                </Text>
              </div>
            )}
          </ScrollArea>
        </aside>

        {/* Main content */}
        <main className="flow360-v5-main">
          <section className="v5-panel flow360-v5-detail-panel">
            {!selectedFolder && (
              <div className="flow360-v5-empty-state">
                <Icons.Flowfield size={28} />
                <h1>Select a Flow360 project or folder</h1>
                <p>Browse your account hierarchy, inspect geometry and mesh lineage, then import completed work into the current Thread project.</p>
              </div>
            )}

            {selectedFolder && (
              <>
                <header className="v5-panel-header flow360-v5-project-header">
                  <div>
                    <span className="v5-eyebrow">
                      {selectedIsWorkspaceFolder ? "Selected Flow360 folder" : "Selected Flow360 project"}
                    </span>
                    <h2>{selectedFolder.name}</h2>
                    {selectedFolderNode && selectedFolderNode.path.length > 1 && (
                      <Text size="1" color="gray">
                        {selectedFolderNode.path.map((folder) => folder.name).join(" / ")}
                      </Text>
                    )}
                  </div>
                  <div className="v5-panel-actions">
                    <StatusBadge
                      status={
                        selectedFolder.rootItemDisplayStatus ?? selectedFolder.rootItemStatus
                      }
                    />
                  </div>
                </header>

                <Flow360ThreadLinksPanel links={threadProjectLinks(selectedFolder)} />

                {selectedIsWorkspaceFolder ? (
                  <div className="flow360-v5-empty-state is-small">
                    <Icons.Folder size={24} />
                    <h1>{selectedFolderNode?.children.length ?? 0} items in this folder</h1>
                    <p>Select a child project in the left hierarchy to inspect geometry, mesh lineage, and cases.</p>
                  </div>
                ) : (
                  <ScrollArea className="flow360-v5-tree-scroll">
                    {geoLoading && (
                      <div className="flow360-v5-loading">
                        <span className="flow360-v5-loading-dot" />
                        <Text size="2">Loading geometries...</Text>
                      </div>
                    )}

                    {geometries?.length === 0 && (
                      <div className="flow360-v5-empty-state is-small">
                        <GeometryIcon size={24} />
                        <h1>No geometries in this project</h1>
                        <p>Choose another Flow360 project or refresh after new geometry is available.</p>
                      </div>
                    )}

                    {geometries?.map((geom) => (
                      <GeometryGroup key={geom.id} geom={geom} />
                    ))}
                  </ScrollArea>
                )}
              </>
            )}
          </section>
        </main>
      </div>

      {/* Bottom bar */}
      {selectedFolder && !selectedIsWorkspaceFolder && (
        <footer className="flow360-v5-import-bar">
          <Text size="2">
            {geometries?.length ?? 0} geometries in {selectedFolder.name}
          </Text>
          <button className="v5-primary-button" type="button" onClick={() => setImportOpen(true)}>
            <Icons.Plus size={14} />
            Import to Thread
          </button>
        </footer>
      )}

      {selectedFolder && !selectedIsWorkspaceFolder && (
        <ImportDialog
          folderId={selectedFolder.id}
          folderName={selectedFolder.name}
          threadLinks={threadProjectLinks(selectedFolder)}
          preferredProjectId={preferredProjectId}
          returnTo={returnTo}
          open={importOpen}
          onClose={() => setImportOpen(false)}
        />
      )}
    </div>
  );
}
